import { useState, useCallback, useEffect, useRef } from "react";
import AssetTile from "../AssetTile/AssetTile";
import { GroupedInvestmentCategory } from "../../types";
import { useUpdateInvestmentPreferences } from "hooks/investments";
import "./AssetTilesList.scss";

interface AssetTilesListProps {
  categories: Record<string, GroupedInvestmentCategory>;
  title?: string;
  isOwn?: boolean;
}

const AssetTilesList = ({
  categories,
  title,
  isOwn = false,
}: AssetTilesListProps) => {
  const getSortedCategories = useCallback(() => {
    return Object.values(categories).sort((a, b) => a.order - b.order);
  }, [categories]);

  const [sortedCategories, setSortedCategories] =
    useState<GroupedInvestmentCategory[]>(getSortedCategories);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const { mutate: updatePreferences } = useUpdateInvestmentPreferences();

  const touchDragIndex = useRef<number | null>(null);

  const handleTouchStart = (index: number) => {
    touchDragIndex.current = index;
    setDragIndex(index);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const tileEl = el?.closest("[data-tile-index]");
    if (!tileEl) return;
    const overIndex = Number(tileEl.getAttribute("data-tile-index"));
    if (!isNaN(overIndex) && overIndex !== touchDragIndex.current) {
      setDragOverIndex(overIndex);
    }
  };

  const handleTouchEnd = () => {
    handleDragEnd();
    touchDragIndex.current = null;
  };

  useEffect(() => {
    setSortedCategories(getSortedCategories());
  }, [getSortedCategories]);

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragEnter = (index: number) => {
    if (dragIndex === null || dragIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (dragIndex === null || dragOverIndex === null) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newCategories = [...sortedCategories];
    const [draggedItem] = newCategories.splice(dragIndex, 1);
    newCategories.splice(dragOverIndex, 0, draggedItem);

    const reorderedCategories = newCategories.map((cat, index) => ({
      ...cat,
      order: index,
    }));

    setSortedCategories(reorderedCategories);

    const section: "own" | "reliance" = isOwn ? "own" : "reliance";
    const preferences = reorderedCategories.map((cat) => ({
      slug: cat.investmentType.slug,
      section,
      order: cat.order,
      isExpanded: cat.isExpanded,
    }));

    updatePreferences({ preferences });

    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleToggleExpand = (slug: string, isExpanded: boolean) => {
    const updatedCategories = sortedCategories.map((cat) =>
      cat.investmentType.slug === slug ? { ...cat, isExpanded } : cat,
    );

    setSortedCategories(updatedCategories);

    const section: "own" | "reliance" = isOwn ? "own" : "reliance";
    const preferences = updatedCategories.map((cat) => ({
      slug: cat.investmentType.slug,
      section,
      order: cat.order,
      isExpanded: cat.isExpanded,
    }));

    updatePreferences({ preferences });
  };

  if (sortedCategories.length === 0) {
    return null;
  }

  return (
    <div className="asset-tiles-list">
      {title && <h3 className="asset-tiles-list__title">{title}</h3>}
      <div className="asset-tiles-list__items">
        {sortedCategories.map((category, index) => (
          <AssetTile
            key={category.investmentType.slug}
            category={category}
            index={index}
            isOwn={isOwn}
            onToggleExpand={handleToggleExpand}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragEnd={handleDragEnd}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            isDragging={dragIndex === index}
            isDragOver={dragOverIndex === index}
          />
        ))}
      </div>
    </div>
  );
};

export default AssetTilesList;
